import { Injectable } from '@angular/core';
import { ToastrService} from 'ngx-toastr';

/** The message types the toast styles know (see styles/_extras.scss). */
type ToastKind = 'success' | 'danger' | 'warning' | 'info';

/** Colour names pages pass, in any case ('Error', 'note'…), mapped to a style. */
const KIND: Record<string, ToastKind> = {
  success: 'success',
  danger: 'danger',
  error: 'danger',
  warning: 'warning',
  note: 'info',
  info: 'info',
};

/** A second error within this time is the page repeating one already shown. */
const REPEAT_WINDOW_MS = 1500;

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  /** Error toasts are held back until then (see showSignInNeeded). */
  private quietUntil = 0;
  private lastSignInToast = 0;
  private lastErrorAt = 0;
  private lastText = '';
  private lastTextAt = 0;

  constructor(private toastr: ToastrService) { }

  /**
   * "Please sign in" after the server refused a store request for a missing
   * or expired login. Shown at most once every few seconds, and the page's
   * own generic error for the same request ("Could not add this to your
   * cart") is held back so there's one clear message, not two.
   */
  showSignInNeeded(expired: boolean) {
    const now = Date.now();
    if (now - this.lastSignInToast >= 4000) {
      this.lastSignInToast = now;
      this.lastErrorAt = 0; // this message wins over any error shown just before
      this.show(expired ? 'Your sign-in has expired. Please sign in again.' : 'Please sign in first.', 'warning', 6000);
    }
    this.quietUntil = now + 2000;
  }

  /** A short confirmation; pass a colour for anything that isn't a success. */
  showSuccess(message: string, color?: string) {
    const kind = this.kindOf(color, 'success');
    this.show(message, kind, kind === 'success' ? 2500 : 5000);
  }

  /** A message that stays a little longer; neutral (blue) unless a colour is given. */
  showNote(message: string, color?: string) {
    this.show(message, this.kindOf(color, 'info'), 5000);
  }

  private kindOf(color: string | undefined, fallback: ToastKind): ToastKind {
    return (color && KIND[color.toLowerCase()]) || fallback;
  }

  private show(message: string, kind: ToastKind, timeOut: number) {
    if (!message) return;
    const now = Date.now();
    const isError = kind === 'danger' || kind === 'warning';
    // One message per problem: the central error handler shows the server's
    // reason first; a page's own generic error right after it is skipped, as
    // is the same text twice in a row, and errors while "please sign in" shows.
    if (isError && (now < this.quietUntil || now - this.lastErrorAt < REPEAT_WINDOW_MS)) return;
    if (message === this.lastText && now - this.lastTextAt < REPEAT_WINDOW_MS) return;
    if (isError) this.lastErrorAt = now;
    this.lastText = message;
    this.lastTextAt = now;

    this.toastr.success(message, null, {
      closeButton: false,
      timeOut,
      progressBar: true,
      progressAnimation: 'increasing',
      tapToDismiss: true,
      positionClass: 'toast-top-right',
      toastClass: `ngx-custom-toast-${kind}`,
    } as any);
  }
}
